import React, {useState} from 'react';
import {CircularProgress, Stack} from '@mui/material';
import {
  Body,
  Button,
  Icon,
  Label,
  Modal,
  designSystemToken,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {
  PostProductionGeneration,
  POST_PRODUCTION_GENERATION_STATUS,
  RESOLUTION_OPTIONS,
  TargetResolution,
} from '@/types/postProductionGeneration';
import DownloadButton from '@/components/download-button/DownloadButton';
import useResizeAnalytics from '@/hooks/analytics/use-resize-analytics';
import usePostProductionGenerations from '@/hooks/use-post-production-generations';

export type PostProductionGenerationsListProps = {
  testID?: string;
  campaignId: string | undefined;
  assetId: string | undefined;
  creatorId?: string;
  enabled?: boolean;
};

const RESOLUTION_LABELS: Record<TargetResolution, string> =
  RESOLUTION_OPTIONS.reduce(
    (acc, option) => ({...acc, [option.resolution]: option.label}),
    {} as Record<TargetResolution, string>
  );

function StatusContent({
  generation,
  onPlay,
  onTrackPlay,
  onTrackDownload,
}: {
  generation: PostProductionGeneration;
  onPlay: (url: string) => void;
  onTrackPlay?: () => void;
  onTrackDownload?: () => void;
}) {
  const readyContent = generation.url ? (
    <Stack direction="row" gap="4px" alignItems="center">
      <Button
        appearance="neutral"
        mode="plain"
        size="small"
        startIcon={
          <Icon
            name="Features-Play"
            size="small"
            appearance="neutral"
            color="currentColor"
          />
        }
        onClick={() => {
          onTrackPlay?.();
          onPlay(generation.url as string);
        }}
      >
        {translate('Play')}
      </Button>
      <DownloadButton
        id={generation.id}
        url={generation.url}
        onClick={onTrackDownload}
      />
    </Stack>
  ) : (
    <Body size="sm" color={designSystemToken('semantic.fg.secondary')}>
      {translate('Video unavailable')}
    </Body>
  );

  const failedContent = (
    <Stack direction="row" gap="4px" alignItems="center">
      <Icon
        name="Actions-Alert-Line"
        size="small"
        appearance="danger"
        color="currentColor"
      />
      <Body size="sm" color={designSystemToken('semantic.fg.secondary')}>
        {generation.failureReason || translate('Resize failed')}
      </Body>
    </Stack>
  );

  const processingContent = (
    <Stack direction="row" gap="8px" alignItems="center">
      <CircularProgress size={16} color="inherit" />
      <Body size="sm" color={designSystemToken('semantic.fg.secondary')}>
        {translate('Resizing…')}
      </Body>
    </Stack>
  );

  const contentByStatus: Record<
    PostProductionGeneration['status'],
    React.ReactElement
  > = {
    [POST_PRODUCTION_GENERATION_STATUS.READY]: readyContent,
    [POST_PRODUCTION_GENERATION_STATUS.FAILED]: failedContent,
    [POST_PRODUCTION_GENERATION_STATUS.PENDING]: processingContent,
    [POST_PRODUCTION_GENERATION_STATUS.PROCESSING]: processingContent,
  };

  return contentByStatus[generation.status];
}

function GenerationRow({
  generation,
  onPlay,
  onTrackPlay,
  onTrackDownload,
}: {
  generation: PostProductionGeneration;
  onPlay: (url: string) => void;
  onTrackPlay?: () => void;
  onTrackDownload?: () => void;
}) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      data-testid={`post-production-generation-${generation.status}`}
    >
      <Body size="sm" color={designSystemToken('semantic.fg.primary')}>
        {translate(
          RESOLUTION_LABELS[generation.targetResolution] ??
            generation.targetResolution
        )}
      </Body>
      <StatusContent
        generation={generation}
        onPlay={onPlay}
        onTrackPlay={onTrackPlay}
        onTrackDownload={onTrackDownload}
      />
    </Stack>
  );
}

export type PostProductionGenerationsListViewProps = {
  testID?: string;
  generations: PostProductionGeneration[];
  onTrackPlay?: () => void;
  onTrackDownload?: () => void;
};

export function PostProductionGenerationsListView({
  testID = 'post-production-generations-list',
  generations,
  onTrackPlay,
  onTrackDownload,
}: PostProductionGenerationsListViewProps) {
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);

  if (generations.length === 0) {
    return null;
  }

  return (
    <Stack gap="8px" data-testid={testID}>
      <Label size="sm" color={designSystemToken('semantic.fg.secondary')}>
        {translate('Post-production')}
      </Label>
      {generations.map((generation) => (
        <GenerationRow
          key={generation.id}
          generation={generation}
          onPlay={setPlayingUrl}
          onTrackPlay={onTrackPlay}
          onTrackDownload={onTrackDownload}
        />
      ))}

      {playingUrl !== null ? (
        <Modal open handleClose={() => setPlayingUrl(null)} size="large">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            data-testid="post-production-player"
            src={playingUrl}
            controls
            autoPlay
            style={{width: '100%', maxHeight: '80vh'}}
          />
        </Modal>
      ) : null}
    </Stack>
  );
}

function PostProductionGenerationsList({
  testID = 'post-production-generations-list',
  campaignId,
  assetId,
  creatorId,
  enabled = true,
}: PostProductionGenerationsListProps) {
  const {generations} = usePostProductionGenerations({
    campaignId,
    assetId,
    enabled,
  });
  const {trackPlay, trackDownload} = useResizeAnalytics({
    campaignId,
    creatorId,
    flowId: assetId,
  });

  return (
    <PostProductionGenerationsListView
      testID={testID}
      generations={generations}
      onTrackPlay={trackPlay}
      onTrackDownload={trackDownload}
    />
  );
}

export default PostProductionGenerationsList;
